/* step content as panels (consumed by every direction) */
import { BRAND } from "../brand.js";
import React from "react";
import { Icon } from "../components/Icon.jsx";
import {
  Field, TextField, CurrencyField, TextArea, Select, TileSelect, MultiSelect, SearchSelect, YesNo, Toggle,
  MoneySlider, Stepper, AddressField, RegoField,
} from "../components/fields.jsx";
import { ExpenseGrid, DynamicList, IllionPanel, SuppliedStatementsPanel } from "../components/widgets.jsx";
import { OPT, BALLOON_OPTIONS, fmt0, liveEstimate } from "../data/data.js";
import { ANZSIC_OPTIONS } from "../data/anzsic.js";
import { RelatedParties } from "../components/parties.jsx";
import { seedPartiesFromLead, syncPrimaryToLead, partiesIssues } from "../lib/parties.js";
import { consentsFor, consentsSatisfied, consentsError } from "../data/consents.jsx";

export const PHONE_OK = (p) => /^04\d{8}$/.test((p || "").replace(/\s/g, ""));
export const EMAIL_OK = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e || "");
export const DOB_OK = (d) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d || "")) return false;
  const t = new Date(d + "T00:00:00");
  if (Number.isNaN(t.getTime())) return false;
  const age = (Date.now() - t.getTime()) / (365.25 * 24 * 3600 * 1000);
  return age >= 18 && age <= 120;
};
const annualGross = (f) => {
  const m = { WEEKLY: 52, FORTNIGHTLY: 26, MONTHLY: 12, ANNUALLY: 1 }[f.incomePeriod] || 1;
  return (Number(f.grossSalary) || 0) * m;
};
const estNet = (f) => Math.round(annualGross(f) * 0.785);

/* row helper */
function Row2({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
}


/* The asset-finance entity panel. The ABN comes first and does the work:
   one look-up against the Australian Business Register fills the legal name,
   the entity type and the trading names, and tells us two things nobody
   should have to type - how long the ABN has been active, and whether the
   business is registered for GST.

   Three deliberate behaviours:
   - The looked-up fields stay EDITABLE. The register lags reality (a name
     changed last week, a type recorded oddly), and the applicant is the one
     who knows.
   - An ABN under six months old STOPS the application, because no amount of
     later paperwork changes it. The panel says so plainly rather than
     failing at submission.
   - If the register is unreachable or unconfigured, the panel says the check
     couldn't run and lets the details be typed. An outage at the ABR is not
     a reason a broker can't lodge. */
function EntityPanel({ f, set, ctx }) {
  const [state, setState] = React.useState("idle"); // idle | checking | done | error
  const [problem, setProblem] = React.useState(null);
  const digits = String(f.abn || "").replace(/\D/g, "");
  const canCheck = digits.length === 11 && !!ctx.lookupAbn;

  const check = async () => {
    if (!canCheck || state === "checking") return;
    setState("checking"); setProblem(null);
    const res = await ctx.lookupAbn(digits);
    if (!res || !res.ok) {
      setProblem(res || { message: "We couldn't check that ABN just now." });
      setState("error");
      // A failed check must not leave a stale verdict behind: whatever the
      // register said last time is no longer what we know.
      set({ abnVerified: false, abnTooNew: null });
      return;
    }
    const r = res.record;
    set({
      abn: r.abn || digits,
      entityName: r.entityName || f.entityName,
      entityType: r.entityType || f.entityType,
      tradingNames: r.tradingNames || [],
      abnVerified: true,
      abnStatus: r.status || "",
      abnActiveSince: r.activeSince || "",
      abnAgeMonths: r.ageMonths,
      abnTooNew: !!r.tooNew,
      gstRegistered: r.gstRegistered,
    });
    setState("done");
  };

  const months = (n) => (n == null ? "-" : n === 1 ? "1 month" : n < 24 ? `${n} months` : `${Math.floor(n / 12)} years`);
  const verified = f.abnVerified === true;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field label="ABN" hint="11 digits - we'll look up the business and check it with the register.">
        <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <TextField inputMode="numeric" value={f.abn}
              onChange={(v) => set({ abn: v, ...(verified || f.abnTooNew != null ? { abnVerified: false, abnTooNew: null, abnAgeMonths: null } : {}) })}
              placeholder="e.g. 51 824 753 556" />
          </div>
          <button type="button" className="pf-btn pf-btn--ghost" style={{ flexShrink: 0, minWidth: 108 }}
            disabled={!canCheck || state === "checking"} onClick={check}>
            {state === "checking" ? "Checking…" : verified ? "Re-check" : "Validate"}
          </button>
        </div>
      </Field>

      {/* The register's answer, in the applicant's language. */}
      {verified && f.abnTooNew !== true && (
        <div className="pf-rise pf-card" style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8, background: "var(--success-tint)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="check" size={17} style={{ color: "var(--success)", flexShrink: 0 }} />
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>Found on the Australian Business Register</span>
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-70)", lineHeight: 1.6 }}>
            Active for {months(f.abnAgeMonths)}
            {f.abnActiveSince ? ` (since ${f.abnActiveSince.split("-").reverse().join("/")})` : ""}
            {" · "}
            {f.gstRegistered === true ? "Registered for GST" : f.gstRegistered === false ? "Not registered for GST" : "GST status unknown"}
          </div>
          {(f.tradingNames || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(f.tradingNames || []).map((n) => (
                <span key={n} style={{ fontSize: 12, padding: "3px 9px", borderRadius: 999, background: "var(--field)", color: "var(--ink-70)" }}>{n}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* The one answer that ends the conversation. */}
      {f.abnTooNew === true && (
        <div className="pf-rise pf-card" style={{ padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start", background: "var(--coral-tint-2)", border: "1px solid var(--coral)" }}>
          <Icon name="info" size={18} style={{ color: "var(--coral)", flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 13, color: "var(--ink-70)", lineHeight: 1.55 }}>
            This ABN has been registered for {months(f.abnAgeMonths)}. We need at least six months of trading history, so we can't take this application forward.
          </span>
        </div>
      )}

      {/* Couldn't check - say so, and let them keep going by hand. */}
      {state === "error" && (
        <div className="pf-rise pf-card" style={{ padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start", background: "var(--field)" }}>
          <Icon name="info" size={18} style={{ color: "var(--info)", flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 13, color: "var(--ink-70)", lineHeight: 1.55 }}>
            {problem && problem.message}{" "}
            {problem && problem.error === "invalid_abn" ? "" : "You can enter the details below and we'll check it with you later."}
          </span>
        </div>
      )}

      <Field label="Business / entity name" hint={verified ? "From the Australian Business Register - correct it if it's out of date." : undefined}>
        <TextField value={f.entityName} autoCap onChange={(v) => set({ entityName: v })} placeholder="e.g. Harbourline Logistics Pty Ltd" />
      </Field>
      <Field label="Entity type">
        <Select value={f.entityType} onChange={(v) => set({ entityType: v })} options={OPT.entityType} />
      </Field>
    </div>
  );
}

/* icon tile-lists read better as a single column on a phone, two-up on desktop */
const tileCols = (ctx) => (ctx && ctx.device === "phone" ? 1 : 2);

export function getPanels(stepId, f, set, ctx) {
  const P = [];
  // Profile hooks (src/data/profiles.js): validator overrides and — applied
  // after the switch — per-panel copy overrides. Both empty on direct.
  const V = { PHONE_OK, EMAIL_OK, DOB_OK, ...((ctx && ctx.profile && ctx.profile.validators) || {}) };
  const isBroker = !!(ctx && ctx.profile && ctx.profile.channel === "broker");
  switch (stepId) {
    /* ---------------- 1 · LEAD ---------------- */
    case "lead":
      P.push({
        key: "name", q: "What's your name?", hint: "As it appears on your licence.",
        valid: () => (f.firstName || "").length >= 2 && (f.lastName || "").length >= 2 && V.DOB_OK(f.dateOfBirth),
        err: () => {
          const need = [];
          if ((f.firstName || "").length < 2) need.push("your first name");
          if ((f.lastName || "").length < 2) need.push("your last name");
          if (!V.DOB_OK(f.dateOfBirth)) need.push("a valid date of birth (you must be 18 or over)");
          return "Please add " + need.join(", ") + ".";
        },
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Row2>
              <Field label="First name"><TextField value={f.firstName} autoCap onChange={(v) => set({ firstName: v })} placeholder="Jordan" /></Field>
              <Field label="Last name"><TextField value={f.lastName} autoCap onChange={(v) => set({ lastName: v })} placeholder="Avery" /></Field>
            </Row2>
            <Field label="Date of birth" hint="You must be 18 or over to apply.">
              <TextField type="date" value={f.dateOfBirth} onChange={(v) => set({ dateOfBirth: v })} />
            </Field>
          </div>
        ),
      });
      P.push({
        key: "contact", q: "How can we reach you?", hint: "We'll text you a link to pick up where you left off.",
        valid: () => V.PHONE_OK(f.phone) && V.EMAIL_OK(f.email),
        err: () => (!V.PHONE_OK(f.phone) && !V.EMAIL_OK(f.email)) ? "Please enter a valid Australian mobile (04XX XXX XXX) and your email address."
          : !V.PHONE_OK(f.phone) ? "Please enter a valid Australian mobile number - 04 followed by 8 digits."
          : "Please enter a valid email address.",
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Field label="Mobile number" hint="Australian mobile, e.g. 0412 345 678"><TextField type="tel" inputMode="numeric" value={f.phone} onChange={(v) => set({ phone: v })} placeholder="04XX XXX XXX" /></Field>
            <Field label="Email"><TextField type="email" inputMode="email" value={f.email} onChange={(v) => set({ email: v })} placeholder="you@email.com" /></Field>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" checked={f.contactOptIn} onChange={(ev) => set({ contactOptIn: ev.target.checked })}
                style={{ width: 20, height: 20, accentColor: "var(--coral)", flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 13.5, color: "var(--ink-70)", lineHeight: 1.45 }}>
                Keep me updated about my application by email or SMS - including a link to finish later. You can opt out anytime.
              </span>
            </label>
          </div>
        ),
      });
      P.push({
        key: "address", q: "Where do you live?",
        valid: () => (f.address || "").length > 6,
        err: "Please enter your home address.",
        render: () => <Field label="Home address"><AddressField value={f.address} onChange={(v) => set({ address: v })} /></Field>,
      });
      // PLACEHOLDER (broker channel): a reference the broker can match to
      // their own system. The real broker field set replaces this when the
      // business rules land — see src/data/profiles.js.
      if (isBroker) {
        P.push({
          key: "brokerRef", q: "Your reference for this application", hint: "Optional - shown on the file so you can match it in your own system.",
          valid: () => true,
          render: () => <Field label="Broker reference (optional)"><TextField value={f.brokerReference || ""} onChange={(v) => set({ brokerReference: v })} placeholder="e.g. CRM-1042" /></Field>,
        });
      }
      break;

    /* ---------------- 2 · VEHICLE ---------------- */
    case "vehicle":
      P.push({
        key: "found", q: "Have you found your car yet?", hint: "Pop in the rego and we'll fetch the details for you.",
        valid: () => f.vehicleFound != null && (f.vehicleFound === false || !!f.vehicle),
        err: () => (f.vehicleFound == null ? "Let us know if you've found your car yet." : "Enter the rego so we can fetch the car's details - or choose \"Still looking\"."),
        render: () => (
          <div>
            <YesNo value={f.vehicleFound} onChange={(v) => set({ vehicleFound: v, ...(v ? {} : { vehicle: null, rego: "" }) })} yesLabel="Yes, found it" noLabel="Still looking" />
            {f.vehicleFound === true && (
              <div className="pf-rise" style={{ marginTop: 18 }}>
                <Field label="Registration plate"><RegoField rego={f.rego} vehicle={f.vehicle} onRego={(v) => set({ rego: v })} onVehicle={(v) => set({ vehicle: v, ...(v && v.bodyType ? { bodyType: v.bodyType.toLowerCase().includes("ute") ? "ute" : v.bodyType.toLowerCase().includes("suv") ? "suv" : "car" } : {}) })} /></Field>
              </div>
            )}
            {f.vehicleFound === false && (
              <p className="pf-hint pf-rise" style={{ marginTop: 14 }}>No worries - you can still get a pre-approval now and add the car later.</p>
            )}
          </div>
        ),
      });
      P.push({
        key: "type", q: "What kind of vehicle?",
        valid: () => !!f.bodyType && f.isNewVehicle != null,
        err: () => (!f.bodyType ? "Pick the type of vehicle." : "Tell us if it's new or used."),
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            <TileSelect value={f.bodyType} onChange={(v) => set({ bodyType: v })} cols={tileCols(ctx)}
              options={[{ v: "car", l: "Car", icon: "car" }, { v: "ute", l: "Ute", icon: "ute" }, { v: "suv", l: "4WD / SUV", icon: "suv" }, { v: "bike", l: "Motorbike", icon: "bike" }]} />
            <div>
              <div className="pf-label" style={{ marginBottom: 10 }}>Is it new or used?</div>
              <div className="pf-tilegrid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                {[[false, "Used"], [true, "New"]].map(([v, l]) => (
                  <button key={l} type="button" className={"pf-tile pf-tile--center" + (f.isNewVehicle === v ? " is-on" : "")} style={{ minHeight: 60 }} onClick={() => set({ isNewVehicle: v })}><span className="pf-tile__title">{l}</span></button>
                ))}
              </div>
            </div>
          </div>
        ),
      });
      P.push({
        key: "amount", q: "How much would you like to borrow?", hint: "Slide to set your amount - your repayment updates live.",
        valid: () => f.loanAmount >= 8000,
        err: "Set your loan amount.",
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            <MoneySlider value={f.loanAmount} onChange={(v) => set({ loanAmount: v })} min={8000} max={50000} step={500} />
            <div>
              <div className="pf-label">Deposit / trade-in (optional)</div>
              <div style={{ marginTop: 6 }}>
                <MoneySlider value={f.depositAmount} onChange={(v) => set({ depositAmount: v })} min={0} max={20000} step={500}
                  sublabel={`Net loan ${fmt0(Math.max(0, f.loanAmount - f.depositAmount))}`} />
              </div>
            </div>
          </div>
        ),
      });
      break;

    /* ---------------- 2 · PURPOSE (personal loan) ---------------- */
    case "purpose":
      P.push({
        key: "purpose", q: "What's the loan for?", hint: "This helps us tailor your rate and term - it won't be held against you.",
        valid: () => !!f.loanPurpose && (f.loanPurpose !== "OTHER" || (f.loanPurposeOther || "").trim().length >= 5),
        err: () => (!f.loanPurpose ? "Pick what the loan is for."
          : "Tell us what the funds will be used for - a sentence is plenty."),
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <TileSelect value={f.loanPurpose} onChange={(v) => set({ loanPurpose: v })} cols={tileCols(ctx)} options={OPT.loanPurpose} />
            {f.loanPurpose === "OTHER" && (
              <Field label="What will the funds be used for?" hint="We need to know what the funds will be used for - the more detail the better.">
                <TextArea value={f.loanPurposeOther} onChange={(v) => set({ loanPurposeOther: v.slice(0, 300) })}
                          placeholder="e.g. Replacing the hot water system and fixing the back fence" rows={3} />
              </Field>
            )}
          </div>
        ),
      });
      P.push({
        key: "amount", q: "How much would you like to borrow?", hint: "Slide to set your amount - your repayment updates live.",
        valid: () => f.loanAmount >= (ctx.amountMin || 2000),
        err: "Set your loan amount.",
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            <MoneySlider value={f.loanAmount} onChange={(v) => set({ loanAmount: v })} min={ctx.amountMin || 2000} max={ctx.amountMax || 50000} step={500} />
          </div>
        ),
      });
      /* The vehicle securing the loan. Optional - someone mid-form isn't
         always next to the car - but asked here because the assessment team
         otherwise chases both by phone on every personal file. Plain inputs,
         no lookup: this never goes to the lender (their personal module
         rejects vehicle keys), it goes to the team assessing the file. */
      P.push({
        key: "security", q: "The asset this loan is secured against", hint: "If it's handy - the rego and rough odometer reading help us finalise faster. You can skip this and we'll ask later.",
        valid: () => true,
        render: () => (
          <Row2>
            <Field label="Registration plate">
              <TextField value={f.securityRego} onChange={(v) => set({ securityRego: v.toUpperCase().replace(/[^A-Z0-9 -]/g, "").slice(0, 10) })} placeholder="e.g. ABC 123" />
            </Field>
            <Field label="Odometer (km)">
              <TextField value={f.securityOdometer} onChange={(v) => set({ securityOdometer: v.replace(/\D/g, "").slice(0, 7) })} placeholder="e.g. 84000" inputMode="numeric" />
            </Field>
          </Row2>
        ),
      });
      break;

    /* ---------------- 2 · ENTITY (commercial asset finance) ----------------
       PLACEHOLDER: minimum business details for a capture-only asset-finance
       file. The real commercial step set (directors, guarantors, the asset
       itself…) replaces this when the business rules land. */
    case "entity":
      P.push({
        // ABN FIRST: one lookup fills the name, the type and the trading
        // names, tells us how old the business is and whether it's registered
        // for GST. Everything below it is the answer, editable if the
        // register is out of date.
        key: "business", q: "Tell us about the business", hint: "Start with the ABN - we'll look the rest up.",
        valid: () => {
          // An ABN under the minimum can't be financed, and that's the one
          // thing here that STOPS the application.
          if (f.abnTooNew === true) return false;
          return (f.entityName || "").trim().length >= 2 && !!f.entityType;
        },
        err: () => (f.abnTooNew === true
          ? `This ABN has only been registered for ${f.abnAgeMonths === 1 ? "1 month" : `${f.abnAgeMonths || 0} months`} - we need at least 6 months of trading history, so we can't take this one forward.`
          : !(f.entityName || "").trim() ? "Enter the business or entity name (or look it up from the ABN)."
          : "Pick the entity type."),
        render: () => <EntityPanel f={f} set={set} ctx={ctx} />,
      });
      P.push({
        key: "amount", q: "How much would you like to finance?", hint: "Slide to set your amount - indicative only until we price the deal.",
        valid: () => f.loanAmount >= (ctx.amountMin || 10000),
        err: "Set your finance amount.",
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            <MoneySlider value={f.loanAmount} onChange={(v) => set({ loanAmount: v })} min={ctx.amountMin || 10000} max={ctx.amountMax || 500000} step={5000} />
            <div>
              <div className="pf-label">Deposit / trade-in (optional)</div>
              <div style={{ marginTop: 6 }}>
                <MoneySlider value={f.depositAmount} onChange={(v) => set({ depositAmount: v })} min={0} max={100000} step={5000}
                  sublabel={`Net amount ${fmt0(Math.max(0, f.loanAmount - f.depositAmount))}`} />
              </div>
            </div>
          </div>
        ),
      });
      break;

    /* ---------------- YOUR BUSINESS SITUATION (asset finance) ----------------
       What the business does and how it runs. Assessment reads these directly,
       so they're captured as structured answers, not prose — except the
       "what do you sell or do" line, which is deliberately free text because
       no category list survives contact with a real small business. */
    case "situation":
      P.push({
        key: "industry", group: "What the business does", q: "What industry is the business in?",
        hint: "Start typing - we'll match it to the standard industry list (ANZSIC).",
        valid: () => !!f.anzsicCode,
        err: "Pick the closest industry match.",
        render: () => (
          <Field label="Industry" hint="Search by what you do, e.g. “plumbing”, “cafe”, “freight”.">
            <SearchSelect value={f.anzsicCode} label={f.anzsicLabel} options={ANZSIC_OPTIONS}
              placeholder="Search industries…"
              onChange={(o) => set({ anzsicCode: o.v, anzsicLabel: o.l })} />
          </Field>
        ),
      });
      P.push({
        key: "activity", group: "What the business does", q: "What specifically does the business sell or do?",
        hint: "A sentence is plenty - it tells us more than a category ever could.",
        valid: () => (f.businessActivity || "").trim().length >= 10,
        err: "Tell us in a sentence what the business sells or does (at least 10 characters).",
        render: () => (
          <Field label="In your own words">
            <TextArea value={f.businessActivity} rows={3} onChange={(v) => set({ businessActivity: v })}
              placeholder="e.g. We install and service commercial refrigeration for restaurants across Brisbane." />
          </Field>
        ),
      });
      P.push({
        // Optional: useful colour for the assessor, but never worth stopping
        // an application over - the bank statements show how money arrives.
        key: "paid", group: "How the business runs", q: "How does the business get paid?",
        hint: "Tick all that apply - or skip it.",
        valid: () => true,
        render: () => <MultiSelect value={f.paymentMethods} onChange={(v) => set({ paymentMethods: v })}
          cols={tileCols(ctx)} options={OPT.paymentMethods} />,
      });
      P.push({
        key: "premises", group: "How the business runs", q: "Where is the business run from?",
        valid: () => !!f.businessPremises,
        err: "Pick where the business operates from.",
        render: () => <TileSelect value={f.businessPremises} onChange={(v) => set({ businessPremises: v })}
          cols={tileCols(ctx)} options={OPT.businessPremises} />,
      });
      P.push({
        key: "employees", group: "How the business runs", q: "How many employees are there?",
        hint: "Not counting the owners.",
        valid: () => !!f.employeeCount,
        err: "Pick the number of employees.",
        render: () => <TileSelect value={f.employeeCount} onChange={(v) => set({ employeeCount: v })}
          cols={tileCols(ctx)} options={OPT.employeeCount} />,
      });
      P.push({
        key: "tax", group: "Tax", q: "What's the business's current tax position?",
        hint: "An ATO balance doesn't rule anything out - we just need the full picture.",
        valid: () => !!f.taxPosition,
        err: "Pick the current tax position.",
        render: () => <TileSelect value={f.taxPosition} onChange={(v) => set({ taxPosition: v })}
          cols={tileCols(ctx)} options={OPT.taxPosition} />,
      });
      break;

    /* ---------------- WHO'S BEHIND THE BUSINESS (asset finance) ----------------
       Replaces the single-applicant "customer" step: directors, partners,
       trustees and guarantors, one of whom is the main contact. That choice
       is load-bearing — their details become the ones we email. */
    case "parties": {
      const parties = f.parties && f.parties.length ? f.parties : seedPartiesFromLead(f);
      const setParties = (next) => set({ parties: next, ...syncPrimaryToLead(next) });
      const issues = partiesIssues(parties);
      P.push({
        key: "people", q: "Who's behind the business?",
        hint: "Everyone with an ownership or guarantee interest - directors, partners, trustees and guarantors.",
        valid: () => issues.length === 0,
        err: () => "Please " + issues.join("; ") + ".",
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <RelatedParties parties={parties} onChange={setParties} />
            <p className="pf-hint">
              The <b>main contact</b> is who we'll email about this application - you can change which
              person that is at any time.
            </p>
          </div>
        ),
      });
      break;
    }

    /* ---------------- HOW THE BUSINESS IS TRADING (asset finance) ----------------
       Replaces the consumer income & expenses step. Deliberately the figures
       an owner knows off the top of their head — turnover and profit from the
       last full year, and whether this year is better or worse. Anything
       finer belongs in the financials the assessor asks for later, not in an
       intake form. */
    case "financials":
      P.push({
        // Turnover only. Gross and net profit were dropped from intake on
        // 2026-08-16: an owner rarely has them to hand mid-form, and the
        // assessor takes them from the financials and statements anyway.
        // Both remain editable in the console for exactly that reason.
        key: "lastyear", group: "Last full financial year", q: "What did the business turn over?",
        hint: "Total sales before any costs - a close estimate is fine.",
        valid: () => Number(f.annualTurnover) > 0,
        err: "Enter the annual turnover for the last full financial year.",
        render: () => (
          <Field label="Annual turnover"><CurrencyField value={f.annualTurnover} onChange={(v) => set({ annualTurnover: v })} placeholder="0" /></Field>
        ),
      });
      P.push({
        key: "trend", group: "This year", q: "How is this year tracking?",
        valid: () => !!f.tradingTrend,
        err: "Let us know how this year compares.",
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <TileSelect value={f.tradingTrend} onChange={(v) => set({ tradingTrend: v })} cols={tileCols(ctx)} options={OPT.tradingTrend} />
            <Field label="Anything we should know? (optional)" hint="A big contract won, a quiet quarter, a one-off cost - context helps.">
              <TextArea rows={2} value={f.tradingNote} onChange={(v) => set({ tradingNote: v })} />
            </Field>
          </div>
        ),
      });
      P.push({
        // Optional, and it takes either shape a real small business uses: a
        // set monthly figure, or no wage at all with the owners living on
        // whatever cashflow is left. The second is an ANSWER, not a blank -
        // picking it is how an owner says "there is no drawings figure".
        key: "drawings", group: "This year", q: "What do the owners take out of the business each month?",
        hint: "Optional - wages, drawings or dividends, whatever the owners actually live on.",
        valid: () => true,
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <TileSelect value={f.ownerDrawingsBasis} onChange={(v) => set({ ownerDrawingsBasis: v, ...(v === "LEFTOVER" ? { ownerDrawingsMonthly: "" } : {}) })}
              cols={tileCols(ctx)} options={OPT.ownerDrawingsBasis} />
            {f.ownerDrawingsBasis === "AMOUNT" && (
              <div className="pf-rise">
                <Field label="Owner wages or drawings, per month">
                  <CurrencyField value={f.ownerDrawingsMonthly} onChange={(v) => set({ ownerDrawingsMonthly: v })} placeholder="0" />
                </Field>
              </div>
            )}
          </div>
        ),
      });
      break;

    /* ---------------- 3 · CUSTOMER ---------------- */
    case "customer":
      P.push({
        key: "existing", q: `Are you an existing ${BRAND.name} customer?`,
        valid: () => f.isExistingCustomer != null,
        err: "Choose yes or no.",
        render: () => <YesNo value={f.isExistingCustomer} onChange={(v) => set({ isExistingCustomer: v })} />,
      });
      P.push({
        key: "home", group: "Where you live", q: "How long have you lived there?",
        valid: () => f.yearsAtAddress >= 3 || (f.previousAddress || "").length > 6,
        err: "You've been at your address under 3 years - please add your previous address.",
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {f.address && (
              <div className="pf-card" style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, background: "var(--field)" }}>
                <Icon name="pin" size={17} style={{ color: "var(--coral)", flexShrink: 0 }} />
                <span style={{ fontSize: 13.5, color: "var(--ink-70)" }}>{f.address}</span>
              </div>
            )}
            <Field label="Years at this address">
              <Stepper value={f.yearsAtAddress} onChange={(v) => set({ yearsAtAddress: v })} min={0} max={40} suffix="yrs" />
            </Field>
            {f.yearsAtAddress < 3 && (
              <Field label="Previous address" hint="Required as you've been here under 3 years."><AddressField value={f.previousAddress} onChange={(v) => set({ previousAddress: v })} /></Field>
            )}
            <Field label="Is your postal address the same?">
              <Toggle value={f.postalSame} onChange={(v) => set({ postalSame: v })} labelOn="Same as my home address" labelOff="Different - enter below" />
            </Field>
            {!f.postalSame && <AddressField value={f.postalAddress} onChange={(v) => set({ postalAddress: v })} placeholder="Postal address" />}
          </div>
        ),
      });
      P.push({
        key: "status", group: "Where you live", q: "What's your living situation?",
        valid: () => !!f.residentialStatus,
        err: "Pick your living situation.",
        render: () => <Field label="Residential status"><Select value={f.residentialStatus} onChange={(v) => set({ residentialStatus: v })} options={OPT.residentialStatus} /></Field>,
      });
      P.push({
        key: "marital", group: "About you", q: "What's your relationship status?",
        valid: () => !!f.maritalStatus,
        err: "Pick your relationship status.",
        render: () => <TileSelect value={f.maritalStatus} onChange={(v) => set({ maritalStatus: v })} cols={tileCols(ctx)} options={OPT.maritalStatus} />,
      });
      P.push({
        key: "dependents", group: "About you", q: "How many dependents do you have?", hint: "Someone under 18 you financially support.",
        valid: () => true,
        render: () => <Stepper value={f.numberOfDependents} onChange={(v) => set({ numberOfDependents: v })} min={0} max={10} />,
      });
      P.push({
        key: "residency", group: "About you", q: "What's your residency status?",
        valid: () => !!f.residencyStatus,
        err: "Pick your residency status.",
        render: () => <Field label="Residency"><Select value={f.residencyStatus} onChange={(v) => set({ residencyStatus: v })} options={OPT.residencyStatus} /></Field>,
      });
      break;

    /* ---------------- 4 · IDENTITY ---------------- */
    case "identity":
      P.push({
        key: "documents", q: "Which ID would you like to use?", hint: "Pick one document to confirm your identity - we'll verify it while we process your application.",
        valid: () => {
          if (!f.idDocType) return false;
          if ((f.idDocNumber || "").trim().length < 4) return false;
          if (f.idDocType === "other" && (f.idDocOtherType || "").trim().length < 2) return false;
          return true;
        },
        err: () => (!f.idDocType ? "Pick an ID document to continue."
          : (f.idDocType === "other" && (f.idDocOtherType || "").trim().length < 2) ? "Tell us what type of document it is."
          : "Enter the document number (at least 4 characters)."),
        render: () => {
          const opts = [
            { v: "licence", l: "Driver's licence", icon: "id", numLabel: "Licence number", ph: "e.g. 12345678" },
            { v: "passport", l: "Passport", icon: "doc", numLabel: "Passport number", ph: "e.g. PA1234567" },
            { v: "other", l: "Other document", icon: "wallet", numLabel: "Document number", ph: "e.g. 1234 5678" },
          ];
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {opts.map((o) => {
                const on = f.idDocType === o.v;
                return (
                  <div key={o.v} style={{ display: "flex", flexDirection: "column" }}>
                    <button type="button"
                      className={"pf-tile" + (on ? " is-on" : "")}
                      onClick={() => set(on ? { idDocType: "" } : { idDocType: o.v, idDocNumber: "", idDocOtherType: "" })}
                      style={on ? { boxShadow: "none", borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottomColor: "transparent" } : {}}>
                      <span className="pf-tile__icon"><Icon name={o.icon} size={20} /></span>
                      <span className="pf-tile__body"><span className="pf-tile__title">{o.l}</span></span>
                      <span className="pf-tile__check">{on && <Icon name="check" size={13} />}</span>
                    </button>
                    {on && (
                      <div className="pf-rise" style={{ border: "1.5px solid var(--coral)", borderTop: "1px solid var(--line)", borderRadius: "0 0 var(--r-tile) var(--r-tile)", background: "var(--coral-tint-2)", padding: "16px", display: "flex", flexDirection: "column", gap: 14 }}>
                        {o.v === "other" && (
                          <Field label="Document type"><TextField value={f.idDocOtherType} onChange={(v) => set({ idDocOtherType: v })} placeholder="e.g. Medicare card, Keypass" /></Field>
                        )}
                        <Field label={o.numLabel}><TextField value={f.idDocNumber} onChange={(v) => set({ idDocNumber: v.toUpperCase() })} placeholder={o.ph} /></Field>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        },
      });
      break;

    /* ---------------- 5 · NEEDS ---------------- */
    case "needs":
      P.push({
        key: "term", q: "Over how long would you like to repay?", hint: "A longer term means smaller repayments, but more interest overall.",
        valid: () => !!f.loanTerm,
        err: "Pick a loan term.",
        render: () => {
          return (
            <div className="pf-tilegrid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              {[3, 4, 5].map((yr) => {
                const on = f.loanTerm === yr;
                return (
                  <button key={yr} type="button" className={"pf-tile pf-tile--center" + (on ? " is-on" : "")} style={{ padding: "20px 8px", gap: 6 }} onClick={() => set({ loanTerm: yr })}>
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26 }}>{yr}</span>
                    <span style={{ fontSize: 12.5, color: "var(--ink-45)" }}>years</span>
                  </button>
                );
              })}
            </div>
          );
        },
      });
      // Asset finance only: a balloon (residual) at the end of the term.
      // Capped at 30% - the highest residual the asset-finance book writes -
      // and offered in the three steps ops actually quote. Car and personal
      // never see this panel, so their step set is untouched.
      if (ctx.product === "commercial") {
        P.push({
          key: "balloon", q: "Would you like a balloon at the end?",
          hint: "A balloon (residual) lowers the repayment now and leaves a lump sum owing at the end of the term. Maximum 30%.",
          valid: () => f.balloonPct != null && f.balloonPct !== "",
          err: "Pick a balloon amount, or choose none.",
          render: () => (
            <div className="pf-tilegrid" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
              {BALLOON_OPTIONS.map((pct) => {
                const on = Number(f.balloonPct) === pct;
                return (
                  <button key={pct} type="button" className={"pf-tile pf-tile--center" + (on ? " is-on" : "")} style={{ padding: "18px 6px", gap: 6 }} onClick={() => set({ balloonPct: pct })}>
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22 }}>{pct === 0 ? "None" : `${pct}%`}</span>
                    {pct > 0 && <span style={{ fontSize: 12, color: "var(--ink-45)" }}>{fmt0(Math.round(Math.max(0, (f.loanAmount || 0) - (f.depositAmount || 0)) * pct / 100))}</span>}
                  </button>
                );
              })}
            </div>
          ),
        });
      }
      P.push({
        key: "freq", q: "How often would you like to repay?", hint: "Pick the rhythm that suits your pay cycle - your estimate updates to match.",
        valid: () => !!f.paymentFrequency,
        err: "Pick how often you'd like to repay.",
        render: () => {
          return (
            <div className="pf-tilegrid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              {[["weekly", "Weekly", "wk"], ["fortnightly", "Fortnightly", "fn"], ["monthly", "Monthly", "mo"]].map(([v, l, w]) => {
                const on = f.paymentFrequency === v;
                return (
                  <button key={v} type="button" className={"pf-tile pf-tile--center" + (on ? " is-on" : "")} style={{ padding: "18px 8px", gap: 6 }} onClick={() => set({ paymentFrequency: v })}>
                    <span className="pf-tile__title" style={{ fontSize: 15 }}>{l}</span>
                  </button>
                );
              })}
            </div>
          );
        },
      });
      P.push({
        key: "aware", q: "Anything that might affect your repayments?", hint: "Upcoming changes to your income, expenses or circumstances.",
        valid: () => f.repaymentAwareness != null,
        err: "Choose yes or no - answering won't automatically affect your application.",
        render: () => (
          <div>
            <YesNo value={f.repaymentAwareness} onChange={(v) => set({ repaymentAwareness: v })} yesLabel="Yes, there is" noLabel="No, all good" />
            {f.repaymentAwareness === true && (
              <div className="pf-rise" style={{ marginTop: 14 }}>
                <Field label="Tell us a little more (optional)"><TextArea value={f.repaymentIssueDetails} onChange={(v) => set({ repaymentIssueDetails: v })} placeholder="e.g. planning to reduce hours next year…" /></Field>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 16, padding: "12px 14px", background: "var(--field)", borderRadius: 12 }}>
              <Icon name="info" size={18} style={{ color: "var(--info)", flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 13, color: "var(--ink-70)", lineHeight: 1.5 }}>This won't automatically affect your application - we just want to understand your situation.</span>
            </div>
          </div>
        ),
      });
      break;

    /* ---------------- 6 · BANK ---------------- */
    case "bank":
      P.push({
        key: "verify", q: "Verify your income in 60 seconds", hint: `Connecting your bank lets us verify your income securely and approve you faster. Powered by illion - securely shared with ${BRAND.name}.`,
        // Asset finance has a second, equally valid way through: the broker
        // already ran illion and hands the statements over (see below). The
        // consumer flows keep exactly one path.
        valid: () => (ctx.product === "commercial"
          ? !!f.illionReference || f.statementsSupplied === true || String(f.illionDocumentId || "").trim().length >= 4
          : !!f.illionReference),
        err: () => (ctx.product === "commercial"
          ? "Connect the bank, or upload the statements you already have (or add the illion document ID)."
          : "Please complete the bank-statements step - or tap \"I've finished this step\" once you've done it in the illion window."),
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <IllionPanel f={f} set={set} onComplete={ctx.onIllionComplete} initiate={ctx.initiateBankSession} status={ctx.bankSessionStatus} relay={ctx.relayBankEvent} />
            {ctx.product === "commercial" && <SuppliedStatementsPanel f={f} set={set} supply={ctx.supplyStatements} />}
          </div>
        ),
      });
      break;

    /* ---------------- 7 · INCOME ---------------- */
    case "income":
      P.push({
        key: "job", q: "Tell us about your work",
        valid: () => {
          if (!f.employmentType || (f.occupationName || "").length < 2) return false;
          if (["RETIRED", "UNEMPLOYED"].includes(f.employmentType)) return true;
          // The lender requires the basis (full/part-time…) when employed.
          if (["PAYG", "SELF_EMPLOYED"].includes(f.employmentType) && !f.employmentTime) return false;
          // Lenders require a way to verify employment.
          return (f.employerContactNumber || "").replace(/\D/g, "").length >= 8;
        },
        err: () => (!f.employmentType ? "Pick your employment type."
          : ["PAYG", "SELF_EMPLOYED"].includes(f.employmentType) && !f.employmentTime ? "Pick your employment basis - full time, part time or casual."
          : (f.occupationName || "").length < 2 ? "Enter your role."
          : "Enter your employer's contact number (at least 8 digits) - the lender uses it to verify employment."),
        render: () => {
          const working = !!f.employmentType && !["RETIRED", "UNEMPLOYED"].includes(f.employmentType);
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Field label="Employment type"><Select value={f.employmentType} onChange={(v) => set({ employmentType: v })} options={OPT.employmentType} /></Field>
              {["PAYG", "SELF_EMPLOYED"].includes(f.employmentType) && (
                <Field label="Employment basis" hint="The lender needs to know whether the role is full time, part time or casual.">
                  <Select value={f.employmentTime} onChange={(v) => set({ employmentTime: v })} options={OPT.employmentTime} />
                </Field>
              )}
              <Field label="Employer"><TextField value={f.employerCompanyName} onChange={(v) => set({ employerCompanyName: v })} placeholder="Company name" /></Field>
              {working && (
                <Field label="Employer contact number" hint="Reception or HR is fine - your lender may call to confirm employment.">
                  <TextField type="tel" value={f.employerContactNumber} onChange={(v) => set({ employerContactNumber: v })} placeholder="e.g. 03 9000 1234" />
                </Field>
              )}
              <Field label="Your role"><TextField value={f.occupationName} onChange={(v) => set({ occupationName: v })} placeholder="e.g. Registered nurse" /></Field>
              <Row2>
                <Field label="Started"><TextField type="month" value={f.commencementDate} onChange={(v) => set({ commencementDate: v })} /></Field>
                <Field label="On probation?"><div style={{ paddingTop: 6 }}><Toggle value={f.isUnderProbation} onChange={(v) => set({ isUnderProbation: v })} /></div></Field>
              </Row2>
            </div>
          );
        },
      });
      P.push({
        key: "salary", q: "What's your income?",
        valid: () => !!f.grossSalary,
        err: "Enter your gross income.",
        render: () => (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Row2>
              <Field label="Gross income"><CurrencyField value={f.grossSalary} onChange={(v) => set({ grossSalary: v })} /></Field>
              <Field label="Per"><Select value={f.incomePeriod} onChange={(v) => set({ incomePeriod: v })} options={OPT.incomePeriod} /></Field>
            </Row2>
            {!!f.grossSalary && (
              <div className="pf-rise" style={{ display: "flex", gap: 10, alignItems: "center", padding: "12px 14px", background: "var(--success-tint)", borderRadius: 12 }}>
                <Icon name="info" size={18} style={{ color: "var(--success)" }} />
                <span style={{ fontSize: 13.5, color: "var(--ink-70)" }}>Estimated net income <strong>{fmt0(estNet(f))}/yr</strong> - we'll confirm from your statements.</span>
              </div>
            )}
          </div>
        ),
      });
      P.push({
        key: "expenses", q: "Your monthly expenses", hint: "These are your personal share of the household's costs - enter your typical monthly amounts.",
        valid: () => true,
        render: () => <ExpenseGrid f={f} set={set} />,
      });
      break;

    /* ---------------- 8 · ASSETS ---------------- */
    case "assets":
      P.push({
        key: "assets", q: "What do you own?", hint: "Add anything of value - property, vehicles, savings, super.",
        valid: () => f.assets.length > 0 || f.noAssets,
        err: "Add at least one asset - or tick the box to declare none.",
        render: () => <DynamicList kind="asset" items={f.assets} onChange={(v) => set({ assets: v })} options={OPT.assetType} none={f.noAssets} onNone={(v) => set({ noAssets: v })} />,
      });
      P.push({
        key: "liabilities", q: "What do you owe?", hint: "Loans, credit cards, buy-now-pay-later and other debts.",
        valid: () => f.liabilities.length > 0 || f.noLiabilities,
        err: "Add your debts - or tick the box to declare none.",
        render: () => <DynamicList kind="liability" items={f.liabilities} onChange={(v) => set({ liabilities: v })} options={OPT.liabilityType} none={f.noLiabilities} onNone={(v) => set({ noLiabilities: v })} />,
      });
      break;

    /* ---------------- 9 · REVIEW ---------------- */
    case "review": {
      // One list drives the tick boxes, the gate and the message — see
      // src/data/consents.jsx (commercial gets a different document set).
      const consents = consentsFor({ product: ctx && ctx.product, channel: ctx && ctx.profile && ctx.profile.channel });
      P.push({
        key: "review", q: "Almost there - review & submit", hide: true,
        valid: () => consentsSatisfied(f, consents),
        err: () => consentsError(f, consents),
        render: () => <ReviewSummary f={f} ctx={ctx} set={set} />,
      });
      break;
    }
  }
  // Per-panel copy overrides from the profile, keyed "stepId.panelKey" —
  // { q, hint } replace the built-ins (the broker "you" -> "your client"
  // rewrite lands here without touching the panels above).
  const copy = (ctx && ctx.profile && ctx.profile.copy) || {};
  for (const p of P) {
    const o = copy[`${stepId}.${p.key}`];
    if (o) { if (o.q) p.q = o.q; if (o.hint !== undefined) p.hint = o.hint; }
  }
  return P;
}

/* ============================================================
   Review summary (Step 9)
   ============================================================ */
export function ReviewSummary({ f, ctx, set }) {
  const lab = (arr, v) => (arr.find((o) => o.v === v) || {}).l || "-";
  const dmy = (iso) => (iso ? iso.split("-").reverse().join("/") : "-");
  const e = liveEstimate(f);
  const freqLabel = { weekly: "Weekly", fortnightly: "Fortnightly", monthly: "Monthly" }[f.paymentFrequency] || "-";
  const freqWord = { weekly: "week", fortnightly: "fortnight", monthly: "month" }[e.freq];
  // Sections are keyed by STEP ID, not number: the commercial sequence has ten
  // steps and a different order, so a hard-coded "Edit -> step 7" would land on
  // the wrong screen. num() resolves the id against the flow's own step list.
  const num = (id) => { const s = (ctx.steps || []).find((x) => x.id === id); return s ? s.num : 1; };
  const identityRows = [["Document", f.idDocType === "licence" ? "Driver's licence" : f.idDocType === "passport" ? "Passport" : f.idDocType === "other" ? (f.idDocOtherType || "Other document") : "-"], ["Number", f.idDocNumber || "-"]];
  const termRows = [["Term", `${f.loanTerm} years`], ["Frequency", freqLabel],
    ...(ctx.product === "commercial" ? [["Balloon", Number(f.balloonPct) > 0 ? `${Number(f.balloonPct)}%` : "None"]] : []),
    ["Est. repayment", `${fmt0(e.amount)} / ${freqWord}`]];
  const stackRows = [["Assets", f.noAssets ? "None declared" : `${f.assets.length} listed`], ["Liabilities", f.noLiabilities ? "None declared" : `${f.liabilities.length} listed`]];
  const parties = f.parties || [];
  const primary = parties.find((p) => p.isPrimary) || parties[0] || null;
  const sections = ctx.product === "commercial"
    ? [
      { step: num("lead"), title: "Main contact", rows: [["Name", `${f.firstName} ${f.lastName}`.trim() || "-"], ["Mobile", f.phone], ["Email", f.email]] },
      { step: num("entity"), title: "Business & finance", rows: [["Entity", f.entityName || "-"], ["Type", lab(OPT.entityType, f.entityType)], ["ABN", f.abn || "-"], ["Amount", fmt0(f.loanAmount)], ["Deposit", fmt0(f.depositAmount)]] },
      { step: num("situation"), title: "Your business situation", rows: [["Industry", f.anzsicLabel || "-"], ["Employees", lab(OPT.employeeCount, f.employeeCount)], ["Tax position", lab(OPT.taxPosition, f.taxPosition)]] },
      { step: num("parties"), title: "Who's behind the business", rows: [["People", parties.length ? `${parties.length} listed` : "-"], ["Main contact", primary ? `${primary.firstName || ""} ${primary.lastName || ""}`.trim() || "-" : "-"]] },
      { step: num("identity"), title: "Identity", rows: identityRows },
      { step: num("needs"), title: "Loan term", rows: termRows },
      { step: num("financials"), title: "Business financials", rows: [["Annual turnover", fmt0(f.annualTurnover)], ["This year vs last", lab(OPT.tradingTrend, f.tradingTrend)], ["Owner drawings", f.ownerDrawingsBasis === "LEFTOVER" ? "No set wage - what's left over" : f.ownerDrawingsBasis === "AMOUNT" ? `${fmt0(f.ownerDrawingsMonthly)} / month` : "-"]] },
      { step: num("assets"), title: "Assets & liabilities", rows: stackRows },
    ]
    : [
      { step: num("lead"), title: "Your details", rows: [["Name", `${f.firstName} ${f.lastName}`.trim() || "-"], ["Date of birth", dmy(f.dateOfBirth)], ["Mobile", f.phone], ["Email", f.email], ["Address", f.address]] },
      ((ctx.product || "car") === "personal"
        ? { step: num("purpose"), title: "Loan purpose", rows: [["Purpose", f.loanPurpose === "OTHER" && (f.loanPurposeOther || "").trim() ? `Something else - ${f.loanPurposeOther.trim()}` : lab(OPT.loanPurpose, f.loanPurpose)], ["Loan amount", fmt0(f.loanAmount)], ["Term", `${f.loanTerm} years`],
          // only when given - "Security car: -" on the review reads like an error
          ...(f.securityRego || f.securityOdometer ? [["Security asset", [f.securityRego, f.securityOdometer && `${Number(f.securityOdometer).toLocaleString()} km`].filter(Boolean).join(" · ")]] : [])] }
        : { step: num("vehicle"), title: "Vehicle & loan", rows: [["Vehicle", f.vehicle && !f.vehicle.manual ? `${f.vehicle.year} ${f.vehicle.make} ${f.vehicle.model}` : "To be confirmed"], ["Condition", f.isNewVehicle ? "New" : "Used"], ["Loan amount", fmt0(f.loanAmount)], ["Deposit", fmt0(f.depositAmount)]] }),
      { step: num("customer"), title: "Your situation", rows: [["Living", lab(OPT.residentialStatus, f.residentialStatus)], ["Relationship", lab(OPT.maritalStatus, f.maritalStatus)], ["Dependents", String(f.numberOfDependents)], ["Residency", lab(OPT.residencyStatus, f.residencyStatus)]] },
      { step: num("identity"), title: "Identity", rows: identityRows },
      { step: num("needs"), title: "Loan term", rows: termRows },
      { step: num("income"), title: "Income & expenses", rows: [["Employer", f.employerCompanyName || "-"], ["Role", f.occupationName || "-"], ["Gross income", `${fmt0(annualGross(f))}/yr`]] },
      { step: num("assets"), title: "Assets & liabilities", rows: stackRows },
    ];
  const [open, setOpen] = React.useState(1);
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sections.map((s) => {
          const isOpen = open === s.step;
          return (
            <div key={s.step} className="pf-card" style={{ overflow: "hidden" }}>
              <button type="button" onClick={() => setOpen(isOpen ? -1 : s.step)} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "15px 16px", textAlign: "left" }}>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15.5, flex: 1 }}>{s.title}</span>
                <span className="pf-btn--text" style={{ fontSize: 13.5, color: "var(--coral)" }} onClick={(ev) => { ev.stopPropagation(); ctx.goToStep(s.step); }}>Edit</span>
                <Icon name="chevronDown" size={18} style={{ color: "var(--ink-45)", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
              </button>
              {isOpen && (
                <div style={{ padding: "0 16px 14px" }}>
                  {s.rows.map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0", borderTop: "1px solid var(--line)" }}>
                      <span style={{ fontSize: 13.5, color: "var(--ink-45)" }}>{k}</span>
                      <span style={{ fontSize: 13.5, fontWeight: 500, textAlign: "right", maxWidth: "62%" }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 12 }}>
        {consentsFor({ product: ctx.product, channel: ctx.profile && ctx.profile.channel }).map(({ k, label, req, also }) => (
          <label key={k} style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
            <input type="checkbox" checked={f[k]} onChange={(ev) => set({ [k]: ev.target.checked, ...Object.fromEntries((also || []).map((a) => [a, ev.target.checked])) })} style={{ width: 22, height: 22, accentColor: "var(--coral)", flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 14, color: "var(--ink-70)", lineHeight: 1.45 }}>{label}{req && <span style={{ color: "var(--coral)" }}> *</span>}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
